import { useEffect, useState } from 'react';
import { Sidebar, SidebarDivider as BaseSidebarDivider, SidebarGroup, SidebarItem, SidebarScrollWrapper, SidebarSpace, useSidebarOpenState, sidebarConfig, Link } from '@backstage/core-components'
import { configApiRef, identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react'
import { PortalSidebarSearchModal } from '../search/PortalSidebarSearchModal';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { IconSearch } from '@fastly/beacon-icons';
import { IconFastlyLogoSquare } from '@fastly/beacon-icons/logos';

import { makeStyles } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CategoryIcon from '@material-ui/icons/Category'
import GroupIcon from '@material-ui/icons/People'
import HomeIcon from '@material-ui/icons/Home'
import SettingsIcon from '@material-ui/icons/Settings'

const useSidebarLogoStyles = makeStyles({
  root: {
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    marginLeft: 24,
    display: 'flex',
  },
  title: {
    textWrap: 'nowrap',
    fontSize: '0.875rem',
    fontWeight: 700,
    color: 'var(--COLOR--text--primary)',
    margin: '6px 0 0 6px',
  }
});

const useLogoIconStyles = makeStyles({
  svg: {
    width: 'auto',
    height: 30,
    color: 'var(--COLOR--logo--primary)'
  },
});

const LogoIcon = () => {
  const classes = useLogoIconStyles();

  return <IconFastlyLogoSquare className={classes.svg}/>;
};

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const config = useApi(configApiRef);
  const title = config.getString('app.title');

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? (<><LogoIcon /><span className={classes.title}>{title}</span></>) : <LogoIcon />}
      </Link>
    </div>
  );
};

const SidebarDivider = () => {
  /**
   * Override SidebarDivider styles inline because styleOverrides in the theme
   * currently remove all styles of certain components.
   * https://github.com/backstage/backstage/issues/15427
   */
  return (
    <BaseSidebarDivider
      style={{ backgroundColor: 'var(--COLOR--border--primary)' }}
    />
  );
};

const useUserLabel = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [userLabel, setUserLabel] = useState('Profile');

  useEffect(() => {
    (async () => {
      try {
        // 1) Auth profile (fastest if available)
        const profile = await identityApi.getProfileInfo();
        let label = profile.displayName;

        // 2) Catalog user entity (best for "First Last" in prod)
        if (!label) {
          const identity = await identityApi.getBackstageIdentity(); // "user:default/rmoore"
          const entity = await catalogApi.getEntityByRef(identity.userEntityRef);

          label = (entity as any)?.spec?.profile?.displayName || entity?.metadata?.title || entity?.metadata?.name;

          // 3) Username fallback from entity ref
          if (!label) {
            const ref = identity.userEntityRef;
            label = ref.includes('/') ? ref.split('/').pop() : undefined;
          }
        }

        // 4) Email fallback (if auth provider gave us one)
        if (!label) {
          label = profile.email;
        }

        if (label) setUserLabel(label)
      } catch (err) {
        console.error('Failed to load user label', err);
      }
    })();
  }, [identityApi, catalogApi]);

  return userLabel;
}

export const SidebarContent = NavContentBlueprint.make({
    params: {
        component: ({ navItems }) => {
            // Override Catalog icon here with the correct one due to a bug with the default icon being the Home icon
            // https://github.com/backstage/backstage/blob/master/plugins/catalog/src/alpha/navItems.tsx
            const nav = navItems.withComponent(item => (<SidebarItem icon={() => item.title === 'Catalog'? <CategoryIcon /> : item.icon} to={item.href} text={item.title} />))
            
            const userLabel = useUserLabel()

            return (
                <Sidebar>
                    <SidebarLogo />

                    <SidebarGroup label="Search" icon={<IconSearch />} to="/search">
                        <PortalSidebarSearchModal />
                    </SidebarGroup>

                    <SidebarDivider />

                    <SidebarGroup label="Menu" icon={<MenuIcon />}>
                        <SidebarItem icon={HomeIcon} to="/" text='Home' />

                        {nav.take('page:catalog')}
                        {nav.take('page:techdocs')}
                        {nav.take('page:api-docs')}

                        <MyGroupsSidebarItem singularTitle='My Group' pluralTitle='My Groups' icon={GroupIcon} />

                        {nav.take('page:scaffolder')}

                        <SidebarDivider />
                        <SidebarScrollWrapper>
                        {/* Items in this group will be scrollable if they run out of space */}
                        </SidebarScrollWrapper>
                    </SidebarGroup>

                    <SidebarSpace />
                    <SidebarDivider />

                    <SidebarItem icon={SettingsIcon} to="/settings" text={userLabel} />
                </Sidebar>
            )
        },
    },
})
